import { Component, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges, ElementRef, NgZone, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';

export type PieSlice = {
  label: string;
  value: number;
  color: string;
};

type ComputedSlice = PieSlice & {
  dashArray: string;
  dashOffset: number;
};

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="pie-chart" [style.width]="autoSize ? '100%' : (size + 'px')" [style.height]="autoSize ? '100%' : (size + 'px')">
      <svg
        [attr.width]="autoSize ? baseSize : size"
        [attr.height]="autoSize ? baseSize : size"
        [style.width.%]="autoSize ? 100 : null"
        [style.height.%]="autoSize ? 100 : null"
        [attr.viewBox]="viewBox"
      >
        <!-- Outer ring -->
        <g [attr.transform]="'rotate(' + rotationDeg + ' ' + center + ' ' + center + ')'">
          <ng-container *ngFor="let s of computedOuterSlices; let i = index">
            <circle
              [attr.cx]="center"
              [attr.cy]="center"
              [attr.r]="outerRadius"
              [attr.fill]="'transparent'"
              [attr.stroke]="s.color"
              [attr.stroke-width]="thickness"
              [attr.stroke-dasharray]="hasAnimated ? s.dashArray : ('0 ' + outerCircumference)"
              [attr.stroke-dashoffset]="s.dashOffset"
              [style.transition]="'stroke-dasharray ' + animationDuration + 'ms ease-out ' + (outerBaseDelay + i * animationStagger) + 'ms, opacity ' + animationDuration + 'ms ease-out ' + (outerBaseDelay + i * animationStagger) + 'ms'"
              [style.opacity]="hasAnimated ? 1 : 0"
              stroke-linecap="butt"
            ></circle>
          </ng-container>
        </g>
        <!-- Inner ring (optional) -->
        <g *ngIf="computedInnerSlices.length" [attr.transform]="'rotate(' + rotationDeg + ' ' + center + ' ' + center + ')'">
          <ng-container *ngFor="let s of computedInnerSlices; let i = index">
            <circle
              [attr.cx]="center"
              [attr.cy]="center"
              [attr.r]="innerRadius"
              [attr.fill]="'transparent'"
              [attr.stroke]="s.color"
              [attr.stroke-width]="innerThickness"
              [attr.stroke-dasharray]="hasAnimated ? s.dashArray : ('0 ' + innerCircumference)"
              [attr.stroke-dashoffset]="s.dashOffset"
              [style.transition]="'stroke-dasharray ' + animationDuration + 'ms ease-out ' + (i * animationStagger) + 'ms, opacity ' + animationDuration + 'ms ease-out ' + (i * animationStagger) + 'ms'"
              [style.opacity]="hasAnimated ? 1 : 0"
              stroke-linecap="butt"
            ></circle>
          </ng-container>
        </g>
        <!-- Zoom connectors -->
        <g *ngIf="showZoomConnectors && zoomHasBounds" [attr.transform]="'rotate(' + rotationDeg + ' ' + center + ' ' + center + ')'">
          <line
            *ngIf="connectorStart"
            [attr.x1]="connectorStart?.x1"
            [attr.y1]="connectorStart?.y1"
            [attr.x2]="connectorStart?.x2"
            [attr.y2]="connectorStart?.y2"
            [attr.stroke]="connectorColor"
            [attr.stroke-width]="connectorWidth"
            [attr.stroke-dasharray]="hasAnimated ? (connectorStartLength + ' ' + connectorStartLength) : ('0 ' + connectorStartLength)"
            [style.transition]="'stroke-dasharray ' + animationDuration + 'ms ease-out ' + outerBaseDelay + 'ms, opacity ' + animationDuration + 'ms ease-out ' + outerBaseDelay + 'ms'"
            [style.opacity]="hasAnimated ? 1 : 0"
            stroke-linecap="round"
            stroke-opacity="0.6"
          ></line>
          <line
            *ngIf="connectorEnd"
            [attr.x1]="connectorEnd?.x1"
            [attr.y1]="connectorEnd?.y1"
            [attr.x2]="connectorEnd?.x2"
            [attr.y2]="connectorEnd?.y2"
            [attr.stroke]="connectorColor"
            [attr.stroke-width]="connectorWidth"
            [attr.stroke-dasharray]="hasAnimated ? (connectorEndLength + ' ' + connectorEndLength) : ('0 ' + connectorEndLength)"
            [style.transition]="'stroke-dasharray ' + animationDuration + 'ms ease-out ' + outerBaseDelay + 'ms, opacity ' + animationDuration + 'ms ease-out ' + outerBaseDelay + 'ms'"
            [style.opacity]="hasAnimated ? 1 : 0"
            stroke-linecap="round"
            stroke-opacity="0.6"
          ></line>
        </g>
      </svg>
    </div>
  `,
  styles: [
    `
      .pie-chart { display: inline-block; }
      svg { display: block; }
    `,
  ],
})
export class PieChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  // Outer ring (backward compatible with existing API)
  @Input() slices: PieSlice[] = [];
  @Input() size = 140;
  @Input() thickness = 18;
  // Inner ring (second-level breakdown)
  @Input() innerSlices: PieSlice[] | null = null;
  @Input() innerThickness = 14;
  @Input() ringGap = 4; // space between rings
  // Align inner ring to a specific outer slice label; if provided, the
  // inner ring will start at that slice's start angle and occupy exactly
  // that slice's arc length.
  @Input() innerAlignToOuterLabel: string | null = null;
  // Align outer ring to a specific inner slice label; mirrors the behavior
  // of innerAlignToOuterLabel but in the opposite direction.
  @Input() outerAlignToInnerLabel: string | null = null;
  // If provided (0..1], when aligning rings this overrides the arc length
  // so the aligned sector is expanded to this fraction of the circle.
  @Input() zoomCircleFraction: number | null = null;
  // Crop the SVG viewBox to tightly frame the zoomed outer slice
  @Input() cropToZoomedOuterSlice: boolean = false;
  @Input() viewportPadding: number = 8;
  // Animation
  @Input() animateOnAppear: boolean = true;
  @Input() animationDuration: number = 800; // ms
  @Input() animationStagger: number = 80; // ms per slice
  @Input() animationMode: 'together' | 'innerThenOuter' = 'innerThenOuter';
  // Responsive sizing
  @Input() autoSize: boolean = false;
  @Input() baseSize: number = 200; // intrinsic size used when autoSize=true

  private _computedOuterSlices: ComputedSlice[] = [];
  private _computedInnerSlices: ComputedSlice[] = [];
  private _viewBox: string = '';
  private _center: number = 0;
  private _outerRadius: number = 0;
  private _innerRadius: number = 0;
  private _outerCircumference: number = 0;
  private _innerCircumference: number = 0;
  private _outerStartFractionByLabel: Map<string, number> = new Map();
  private _outerFractionByLabel: Map<string, number> = new Map();
  private _innerStartFractionByLabel: Map<string, number> = new Map();
  private _innerFractionByLabel: Map<string, number> = new Map();
  private _zoomStartFraction: number | null = null;
  private _zoomEndFraction: number | null = null;
  private _zoomCenterFraction: number | null = null;
  private _parentStartFraction: number | null = null;
  private _parentEndFraction: number | null = null;
  private _connectorMode: 'innerParentToOuterZoom' | 'outerParentToInnerZoom' | null = null;
  @Input() showZoomConnectors = false;
  @Input() connectorColor: string = '#ffffff';
  @Input() connectorWidth: number = 2;
  private _hasAnimated = false;
  private _io?: IntersectionObserver;
  private _ro?: ResizeObserver;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone, private cdr: ChangeDetectorRef) {
    this.updateAll();
  }

  get viewBox(): string {
    return this._viewBox;
  }

  ngAfterViewInit(): void {
    if (!this.animateOnAppear) return;
    this.zone.runOutsideAngular(() => {
      this._io = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting) {
          this.zone.run(() => {
            this._hasAnimated = true;
            this.cdr.markForCheck();
          });
          this._io?.disconnect();
        }
      }, { threshold: 0.15 });
      this._io.observe(this.host.nativeElement);
    });

    if (this.autoSize) {
      const measure = () => {
        // No geometry resize; we keep a fixed intrinsic size and scale via CSS
        // We still trigger change detection to ensure layout updates if needed
        this.cdr.markForCheck();
      };
      measure();
      this._ro = new ResizeObserver(() => measure());
      this._ro.observe(this.host.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this._io?.disconnect();
    this._ro?.disconnect();
  }

  get center(): number {
    return this._center;
  }

  get rotationDeg(): number { return -90; }

  get outerRadius(): number {
    return this._outerRadius;
  }

  get innerRadius(): number {
    return this._innerRadius;
  }

  get outerCircumference(): number {
    return this._outerCircumference;
  }

  get innerCircumference(): number {
    return this._innerCircumference;
  }

  get hasAnimated(): boolean {
    return this._hasAnimated;
  }

  get outerBaseDelay(): number {
    if (!this.hasAnimated) return 0; // value only used at render time after trigger
    if (this.animationMode === 'innerThenOuter') {
      const count = (this._computedInnerSlices?.length || 0);
      return this.animationDuration + this.animationStagger * count;
    }
    return 0;
  }

  get computedOuterSlices(): ComputedSlice[] {
    return this._computedOuterSlices;
  }

  get computedInnerSlices(): ComputedSlice[] {
    return this._computedInnerSlices;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['slices'] ||
      changes['innerSlices'] ||
      changes['size'] ||
      changes['thickness'] ||
      changes['innerThickness'] ||
      changes['ringGap']
    ) {
      this.updateAll();
    }
  }

  private updateAll(): void {
    const effectiveSize = this.autoSize ? this.baseSize : this.size;
    this._viewBox = `0 0 ${effectiveSize} ${effectiveSize}`;
    this._center = effectiveSize / 2;
    // Outer ring
    this._outerRadius = Math.max(0, this._center - this.thickness / 2);
    this._outerCircumference = 2 * Math.PI * this._outerRadius;
    const outerMeta = this.computeSlicesWithMeta(this.slices || [], this._outerCircumference);
    this._computedOuterSlices = outerMeta.computed;
    this._outerStartFractionByLabel = outerMeta.startFractionByLabel;
    this._outerFractionByLabel = outerMeta.fractionByLabel;

    // Inner ring (optional)
    if (this.innerSlices && this.innerSlices.length > 0) {
      const proposedInnerRadius = this._outerRadius - (this.thickness / 2 + this.ringGap + this.innerThickness / 2);
      this._innerRadius = Math.max(0, proposedInnerRadius);
      this._innerCircumference = 2 * Math.PI * this._innerRadius;
      // Determine alignment and arc fraction from outer slice label if requested
      let startAt = 0;
      let circleFraction = 1;
      if (this.innerAlignToOuterLabel && this._outerStartFractionByLabel.has(this.innerAlignToOuterLabel)) {
        const startFrac = this._outerStartFractionByLabel.get(this.innerAlignToOuterLabel)!;
        const fracLen = this._outerFractionByLabel.get(this.innerAlignToOuterLabel!) || 0;
        const zoom = this.zoomCircleFraction ?? fracLen;
        const centerFrac = (startFrac + fracLen / 2) % 1;
        // Start so that the zoomed arc is centered on the parent slice
        const startFracZoom = ((centerFrac - zoom / 2) % 1 + 1) % 1;
        startAt = startFracZoom * this._innerCircumference;
        circleFraction = Math.max(0, Math.min(1, zoom));
        this._zoomStartFraction = startFracZoom;
        this._zoomEndFraction = (startFracZoom + circleFraction) % 1;
        this._zoomCenterFraction = centerFrac;
        this._parentStartFraction = startFrac;
        this._parentEndFraction = (startFrac + fracLen) % 1;
        this._connectorMode = 'outerParentToInnerZoom';
      }
      const innerMeta = this.computeSlicesWithMeta(this.innerSlices, this._innerCircumference, { startAt, circleFraction });
      this._computedInnerSlices = innerMeta.computed;
      this._innerStartFractionByLabel = innerMeta.startFractionByLabel;
      this._innerFractionByLabel = innerMeta.fractionByLabel;
    } else {
      this._innerRadius = 0;
      this._innerCircumference = 0;
      this._computedInnerSlices = [];
      this._innerStartFractionByLabel = new Map();
      this._innerFractionByLabel = new Map();
    }

    // Optional: realign OUTER ring based on inner label
    if (this.outerAlignToInnerLabel && this._innerStartFractionByLabel.has(this.outerAlignToInnerLabel)) {
      const startFrac = this._innerStartFractionByLabel.get(this.outerAlignToInnerLabel)!;
      const fracLen = this._innerFractionByLabel.get(this.outerAlignToInnerLabel!) || 0;
      const zoom = this.zoomCircleFraction ?? fracLen;
      const centerFrac = (startFrac + fracLen / 2) % 1;
      // Start so that the zoomed arc is centered on the parent slice
      const startFracZoom = ((centerFrac - zoom / 2) % 1 + 1) % 1;
      const startAt = startFracZoom * this._outerCircumference;
      const circleFraction = Math.max(0, Math.min(1, zoom));
      const outerRealigned = this.computeSlicesWithMeta(this.slices || [], this._outerCircumference, { startAt, circleFraction });
      this._computedOuterSlices = outerRealigned.computed;
      this._outerStartFractionByLabel = outerRealigned.startFractionByLabel;
      this._outerFractionByLabel = outerRealigned.fractionByLabel;
      this._zoomStartFraction = startFracZoom;
      this._zoomEndFraction = (startFracZoom + circleFraction) % 1;
      this._zoomCenterFraction = centerFrac;
      this._parentStartFraction = startFrac;
      this._parentEndFraction = (startFrac + fracLen) % 1;
      this._connectorMode = 'innerParentToOuterZoom';
    }
    // After all geometry updates, optionally crop the view to the zoomed area
    this.updateViewBoxCrop();
  }

  private computeSlicesFor(
    slices: PieSlice[],
    circumference: number,
    options?: { startAt?: number; circleFraction?: number }
  ): ComputedSlice[] {
    return this.computeSlicesWithMeta(slices, circumference, options).computed;
  }

  private computeSlicesWithMeta(
    slices: PieSlice[],
    circumference: number,
    options?: { startAt?: number; circleFraction?: number }
  ): {
    computed: ComputedSlice[];
    startFractionByLabel: Map<string, number>;
    fractionByLabel: Map<string, number>;
  } {
    const sanitized = (slices || []).map((s) => ({
      ...s,
      value: isFinite(s.value) && s.value > 0 ? s.value : 0,
    }));
    const total = sanitized.reduce((acc, s) => acc + s.value, 0);
    if (total <= 0 || circumference <= 0) {
      return { computed: [], startFractionByLabel: new Map(), fractionByLabel: new Map() };
    }

    const startFractionByLabel = new Map<string, number>();
    const fractionByLabel = new Map<string, number>();

    const startAt = options?.startAt ?? 0;
    const circleFraction = options?.circleFraction ?? 1;
    const usableCircumference = circumference * Math.max(0, Math.min(1, circleFraction));

    let accumulated = 0; // along usable arc
    const computed = sanitized.map((s) => {
      const portion = s.value / total;
      const segLength = usableCircumference * portion;
      const dashArray = `${segLength} ${circumference - segLength}`;
      const startOffset = startAt + accumulated;
      const dashOffset = ((circumference - startOffset) % circumference + circumference) % circumference;
      startFractionByLabel.set(s.label, ((startOffset % circumference) + circumference) % circumference / circumference);
      fractionByLabel.set(s.label, segLength / circumference);
      accumulated += segLength;
      return { ...s, dashArray, dashOffset };
    });

    return { computed, startFractionByLabel, fractionByLabel };
  }

  // ===== ViewBox cropping helpers =====
  private normalizeFraction(x: number): number { return ((x % 1) + 1) % 1; }
  private isFracBetween(target: number, start: number, end: number): boolean {
    const t = this.normalizeFraction(target);
    const s = this.normalizeFraction(start);
    const e = this.normalizeFraction(end);
    if (s <= e) return t >= s && t <= e;
    return t >= s || t <= e;
  }
  private forEachAngleInArc(startF: number, endF: number, cb: (f: number) => void): void {
    const candidates = [startF, endF, 0, 0.25, 0.5, 0.75];
    const s = this.normalizeFraction(startF);
    const e = this.normalizeFraction(endF);
    for (const c of candidates) if (this.isFracBetween(c, s, e)) cb(this.normalizeFraction(c));
  }
  private pointOnScreen(radius: number, fraction: number): { x: number; y: number } {
    const angle = 2 * Math.PI * (fraction - 0.25); // account for -90° rotation of groups
    return { x: this._center + radius * Math.cos(angle), y: this._center + radius * Math.sin(angle) };
  }
  private updateViewBoxCrop(): void {
    if (!this.cropToZoomedOuterSlice || this._zoomStartFraction === null || this._zoomEndFraction === null) {
      this._viewBox = `0 0 ${this.size} ${this.size}`;
      return;
    }
    const startF = this._zoomStartFraction as number;
    const endF = this._zoomEndFraction as number;
    const rOuterEdge = this._outerRadius + this.thickness / 2;
    const rInnerEdge = Math.max(0, this._innerRadius - this.innerThickness / 2);

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    this.forEachAngleInArc(startF, endF, (f) => {
      const po = this.pointOnScreen(rOuterEdge, f);
      const pi = this.pointOnScreen(rInnerEdge, f);
      minX = Math.min(minX, po.x, pi.x);
      minY = Math.min(minY, po.y, pi.y);
      maxX = Math.max(maxX, po.x, pi.x);
      maxY = Math.max(maxY, po.y, pi.y);
    });
    const pad = Math.max(0, this.viewportPadding);
    minX -= pad; minY -= pad; maxX += pad; maxY += pad;
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    this._viewBox = `${minX} ${minY} ${width} ${height}`;
  }

  get zoomHasBounds(): boolean {
    return this._zoomStartFraction !== null && this._zoomEndFraction !== null;
  }

  private pointOn(radius: number, fraction: number): { x: number; y: number } {
    const angle = 2 * Math.PI * fraction;
    return {
      x: this._center + radius * Math.cos(angle),
      y: this._center + radius * Math.sin(angle),
    };
  }

  get connectorStart(): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!this.zoomHasBounds) return null;
    const innerOuterEdge = this._innerRadius + this.innerThickness / 2;
    const outerInnerEdge = this._outerRadius - this.thickness / 2;
    if (this._connectorMode === 'innerParentToOuterZoom') {
      const parentF = this._parentStartFraction as number;
      const zoomF = this._zoomStartFraction as number;
      const p1 = this.pointOn(innerOuterEdge, parentF);
      const p2 = this.pointOn(outerInnerEdge, zoomF);
      return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
    } else if (this._connectorMode === 'outerParentToInnerZoom') {
      const parentF = this._parentStartFraction as number;
      const zoomF = this._zoomStartFraction as number;
      const p1 = this.pointOn(outerInnerEdge, parentF);
      const p2 = this.pointOn(innerOuterEdge, zoomF);
      return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
    }
    return null;
  }

  get connectorEnd(): { x1: number; y1: number; x2: number; y2: number } | null {
    if (!this.zoomHasBounds) return null;
    const innerOuterEdge = this._innerRadius + this.innerThickness / 2;
    const outerInnerEdge = this._outerRadius - this.thickness / 2;
    if (this._connectorMode === 'innerParentToOuterZoom') {
      const parentF = this._parentEndFraction as number;
      const zoomF = this._zoomEndFraction as number;
      const p1 = this.pointOn(innerOuterEdge, parentF);
      const p2 = this.pointOn(outerInnerEdge, zoomF);
      return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
    } else if (this._connectorMode === 'outerParentToInnerZoom') {
      const parentF = this._parentEndFraction as number;
      const zoomF = this._zoomEndFraction as number;
      const p1 = this.pointOn(outerInnerEdge, parentF);
      const p2 = this.pointOn(innerOuterEdge, zoomF);
      return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
    }
    return null;
  }

  get connectorStartLength(): number {
    const l = this.connectorStart;
    if (!l) return 0;
    const dx = l.x2 - l.x1;
    const dy = l.y2 - l.y1;
    return Math.sqrt(dx * dx + dy * dy);
  }

  get connectorEndLength(): number {
    const l = this.connectorEnd;
    if (!l) return 0;
    const dx = l.x2 - l.x1;
    const dy = l.y2 - l.y1;
    return Math.sqrt(dx * dx + dy * dy);
  }
}


